import type { AlertLevel, SpendingAlertConfig } from '../../../hooks/useSpendingAlerts';

interface Props {
  level: AlertLevel;
  pctUsed: number;
  monthlyExpenses: number;
  referenceIncome: number;
  usingCustomIncome: boolean;
  config: SpendingAlertConfig;
  onConfigure: () => void;
  onDismiss?: () => void;
}

const fmt = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const LEVEL_STYLES = {
  warning: {
    bar: 'bg-amber-50 border-amber-200',
    icon: 'ri-error-warning-line text-amber-500',
    iconBg: 'bg-amber-100',
    title: 'text-amber-800',
    sub: 'text-amber-600',
    progress: 'bg-amber-400',
    progressBg: 'bg-amber-100',
    badge: 'bg-amber-100 text-amber-700',
    btn: 'bg-amber-500 hover:bg-amber-600 text-white',
  },
  danger: {
    bar: 'bg-red-50 border-red-200',
    icon: 'ri-alarm-warning-line text-red-500',
    iconBg: 'bg-red-100',
    title: 'text-red-800',
    sub: 'text-red-600',
    progress: 'bg-red-500',
    progressBg: 'bg-red-100',
    badge: 'bg-red-100 text-red-700',
    btn: 'bg-red-500 hover:bg-red-600 text-white',
  },
  ok: {
    bar: 'bg-emerald-50 border-emerald-200',
    icon: 'ri-checkbox-circle-line text-emerald-500',
    iconBg: 'bg-emerald-100',
    title: 'text-emerald-800',
    sub: 'text-emerald-600',
    progress: 'bg-emerald-400',
    progressBg: 'bg-emerald-100',
    badge: 'bg-emerald-100 text-emerald-700',
    btn: 'bg-emerald-500 hover:bg-emerald-600 text-white',
  },
};

const LEVEL_LABELS = {
  ok: 'Dentro do orçamento',
  warning: 'Atenção com os gastos',
  danger: 'Limite crítico de gastos',
};

const LEVEL_DESC = {
  ok: (pct: number, inc: number) =>
    `Seus gastos estão em ${pct}% da renda (${fmt(inc)}). Continue assim!`,
  warning: (pct: number, inc: number) =>
    `Seus gastos já chegaram a ${pct}% da renda mensal (${fmt(inc)}). Hora de frear.`,
  danger: (pct: number, inc: number) =>
    `Atenção! Seus gastos ultrapassaram ${pct}% da renda mensal (${fmt(inc)}).`,
};

export const SpendingAlertBanner = ({
  level,
  pctUsed,
  monthlyExpenses,
  referenceIncome,
  usingCustomIncome,
  onConfigure,
  onDismiss,
}: Props) => {
  const s = LEVEL_STYLES[level];
  const barWidth = Math.min(pctUsed, 100);

  return (
    <div
      className={`rounded-2xl border px-5 py-4 flex items-center gap-4 transition-all ${s.bar}`}
    >
      {/* Icon */}
      <div className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${s.iconBg}`}>
        <i className={`${s.icon} text-xl`} />
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <p className={`text-sm font-700 ${s.title}`}>{LEVEL_LABELS[level]}</p>
          <span className={`text-xs font-700 px-2 py-0.5 rounded-full ${s.badge}`}>
            {pctUsed}% da renda
          </span>
          {usingCustomIncome && (
            <span className="text-xs font-500 text-gray-400 italic">renda fixada</span>
          )}
        </div>
        <p className={`text-xs mb-2 ${s.sub}`}>
          {LEVEL_DESC[level](pctUsed, referenceIncome)}
        </p>

        {/* Progress bar */}
        <div className={`h-1.5 rounded-full overflow-hidden ${s.progressBg}`}>
          <div
            className={`h-full rounded-full transition-all duration-500 ${s.progress}`}
            style={{ width: `${barWidth}%` }}
          />
        </div>

        {/* Detail row */}
        <div className="flex items-center gap-4 mt-2 flex-wrap">
          <span className="text-xs text-gray-500">
            <span className="font-600">{fmt(monthlyExpenses)}</span> em despesas
          </span>
          <span className="text-gray-300 text-xs">·</span>
          <span className="text-xs text-gray-500">
            Renda ref.: <span className="font-600">{fmt(referenceIncome)}</span>
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={onConfigure}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-600 text-gray-600 hover:bg-white/70 border border-gray-200/80 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-settings-4-line text-sm" />
          Configurar
        </button>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-white/60 rounded-lg cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-sm" />
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Compact activate prompt (when alerts are disabled) ──────────────────────
interface PromptProps {
  onActivate: () => void;
}

export const SpendingAlertPrompt = ({ onActivate }: PromptProps) => (
  <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/60 px-5 py-3.5 flex items-center gap-3">
    <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-xl shrink-0">
      <i className="ri-alarm-warning-line text-gray-400 text-sm" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-600 text-gray-600">Alertas de gasto desativados</p>
      <p className="text-xs text-gray-400">Ative para receber avisos quando ultrapassar um % da sua renda</p>
    </div>
    <button
      onClick={onActivate}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-600 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
    >
      <i className="ri-add-line text-sm" />
      Ativar alertas
    </button>
  </div>
);
